import React, { useEffect, useState } from "react";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { auth } from "../../../firebase/auth.js";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "../../../shared/context/ThemeContext.jsx";
import { useConfig } from "../../../shared/context/ConfigContext.jsx";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { BackupModal } from "../../backup/components/BackupModal.jsx";
import { ShortcutsSettings } from "../components/ShortcutsSettings.jsx";
import { CompanyList } from "../../companies/components/CompanyList.jsx";
import {
  enableDeleteProtection,
  changeDeletePassword,
  disableDeleteProtection,
  resetDeletePassword
} from "../services/settingsService.js";

// Sheet modes for delete protection
const MODES = {
  enable: { title: "🔒 Set delete password", fields: ["next", "confirm"], submit: "Turn on" },
  change: { title: "🔑 Change delete password", fields: ["current", "next", "confirm"], submit: "Save" },
  disable: { title: "🔓 Turn off delete protection", fields: ["current"], submit: "Turn off" },
  forgot: { title: "❓ Forgot delete password", fields: ["login", "next", "confirm"], submit: "Set new password" }
};
const LABELS = {
  current: "Current delete password",
  next: "New delete password",
  confirm: "Confirm new password",
  login: "Your LOGIN password"
};

function Section({ id, icon, title, description, children, className = "" }) {
  return (
    <Card id={id} className={className} style={{ padding: "1rem", marginBottom: "0.85rem", scrollMarginTop: "72px" }}>
      <div style={{ fontWeight: 800, fontSize: "1rem", color: "var(--text-main)" }}>{icon} {title}</div>
      {description && <div style={{ fontSize: "0.8rem", color: "var(--text-dim)", marginTop: "2px" }}>{description}</div>}
      <div style={{ marginTop: "0.75rem" }}>{children}</div>
    </Card>
  );
}

export function SettingsPage() {
  const { theme, toggleTheme } = useTheme();
  const config = useConfig();
  const { isProtected, refresh } = useDangerConfirm();
  const [mode, setMode] = useState(null);
  const [values, setValues] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [isBackupOpen, setIsBackupOpen] = useState(false);
  const { hash } = useLocation();

  // /settings#companies (e.g. from the dashboard) opens straight on that section
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  const openMode = (m) => {
    setMode(m);
    setValues({});
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cfg = MODES[mode];
    setError("");
    if (cfg.fields.includes("next")) {
      if ((values.next || "").length < 4) return setError("Delete password must be at least 4 characters.");
      if (values.next !== values.confirm) return setError("The new passwords do not match.");
    }
    setSaving(true);
    try {
      if (mode === "enable") await enableDeleteProtection(values.next);
      if (mode === "change") await changeDeletePassword(values.current, values.next);
      if (mode === "disable") await disableDeleteProtection(values.current);
      if (mode === "forgot") {
        const user = auth.currentUser;
        if (!user?.email) throw new Error("Please sign in again.");
        try {
          await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, values.login || ""));
        } catch (_) {
          throw new Error("Login password is wrong.");
        }
        await resetDeletePassword(values.next);
      }
      await refresh();
      setNotice(
        mode === "disable"
          ? "Delete protection is off. Deletes ask for a simple confirmation."
          : "Delete protection is on. Deletes and payment voids now need the delete password."
      );
      setMode(null);
    } catch (err) {
      setError(err.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const segment = (active) => ({
    flex: 1,
    padding: "0.55rem",
    borderRadius: "var(--radius-md)",
    border: active ? "1.5px solid var(--primary)" : "1px solid var(--border-subtle)",
    background: active ? "var(--primary-glow, rgba(59,130,246,0.15))" : "var(--bg-card)",
    color: active ? "var(--primary)" : "var(--text-muted)",
    fontWeight: 700,
    cursor: "pointer"
  });

  return (
    <div className="settings-page">
      <h2 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: "0.85rem" }}>⚙️ Settings</h2>

      {notice && (
        <div style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.35)", color: "var(--success)", padding: "0.6rem 0.85rem", borderRadius: "var(--radius-md)", fontSize: "0.85rem", marginBottom: "0.85rem" }}>
          ✓ {notice}
        </div>
      )}

      {/* Stacked on phones, two columns on desktop */}
      <div className="settings-grid">
        {config?.features?.companies !== false && (
          <Section id="companies" icon="🏢" title="Companies" description="Your billing companies, their invoice numbering and the default company.">
            <CompanyList />
          </Section>
        )}

        <Section icon="🏦" title="Bank accounts & statements" description="Each company's bank accounts; import statement PDFs and view transactions.">
          <Link to="/banking" className="btn btn-secondary" style={{ textDecoration: "none", display: "inline-block" }}>Open Banking</Link>
        </Section>

        <Section icon="🔒" title="Delete protection" description="Ask for a password before deleting records or voiding / resetting payments.">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem" }}>
            <span style={{ fontWeight: 700, color: isProtected ? "var(--success)" : "var(--text-muted)" }}>
              {isProtected ? "ON — password required" : "OFF — simple confirmation"}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={isProtected}
              aria-label="Delete protection"
              onClick={() => openMode(isProtected ? "disable" : "enable")}
              style={{ width: "52px", height: "30px", borderRadius: "15px", border: "none", cursor: "pointer", position: "relative", background: isProtected ? "var(--success)" : "var(--border-subtle)", transition: "background 0.15s" }}
            >
              <span style={{ position: "absolute", top: "3px", left: isProtected ? "25px" : "3px", width: "24px", height: "24px", borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
            </button>
          </div>
          {isProtected && (
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
              <Button type="button" size="sm" variant="secondary" onClick={() => openMode("change")}>🔑 Change password</Button>
              <Button type="button" size="sm" variant="secondary" onClick={() => openMode("forgot")}>❓ Forgot password?</Button>
            </div>
          )}
        </Section>

        {config?.features?.backupGoogleDrive !== false && (
          <Section icon="💾" title="Backup & export" description="Download all business data (ZIP / CSV) to keep a copy.">
            <Button type="button" variant="secondary" onClick={() => setIsBackupOpen(true)}>Open Backup</Button>
          </Section>
        )}

        <Section icon="🎨" title="Appearance">
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button type="button" style={segment(theme === "light")} onClick={() => theme !== "light" && toggleTheme()}>☀️ Light</button>
            <button type="button" style={segment(theme !== "light")} onClick={() => theme === "light" && toggleTheme()}>🌙 Dark</button>
          </div>
        </Section>

        <Section
          icon="⌨️"
          title="Keyboard shortcuts"
          description="Desktop only. Press ? anywhere to see them; change any key here."
          className="desktop-only settings-wide"
        >
          <ShortcutsSettings />
        </Section>
      </div>

      <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", textAlign: "center", marginTop: "0.5rem" }}>
        Signed in as {auth.currentUser?.email || "—"}
      </div>

      <BottomSheet isOpen={!!mode} onClose={() => setMode(null)} title={mode ? MODES[mode].title : ""}>
        {mode && (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {mode === "forgot" && (
              <div style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>
                Confirm it's you with the password you use to sign in, then choose a new delete password.
              </div>
            )}
            {MODES[mode].fields.map((field, i) => (
              <div key={field}>
                <label className="form-label" htmlFor={`settings-${field}`}>{LABELS[field]}</label>
                <input
                  id={`settings-${field}`}
                  type="password"
                  className="form-control"
                  autoComplete={field === "login" ? "current-password" : "new-password"}
                  autoFocus={i === 0}
                  value={values[field] || ""}
                  onChange={(e) => {
                    setValues((v) => ({ ...v, [field]: e.target.value }));
                    setError("");
                  }}
                />
              </div>
            ))}
            {MODES[mode].fields.includes("next") && (
              <div style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>
                At least 4 characters. Using something different from your login password is safer.
              </div>
            )}
            {error && (
              <div style={{ background: "rgba(239, 68, 68, 0.12)", border: "1px solid rgba(239, 68, 68, 0.35)", padding: "0.55rem 0.85rem", borderRadius: "var(--radius-md)", color: "#fca5a5", fontSize: "0.85rem" }}>
                ⚠️ {error}
              </div>
            )}
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <Button type="button" variant="secondary" onClick={() => setMode(null)} style={{ flex: 1 }}>Cancel</Button>
              <Button type="submit" variant={mode === "disable" ? "danger" : "primary"} loading={saving} style={{ flex: 1 }}>
                {MODES[mode].submit}
              </Button>
            </div>
          </form>
        )}
      </BottomSheet>

      <BackupModal isOpen={isBackupOpen} onClose={() => setIsBackupOpen(false)} />
    </div>
  );
}

export default SettingsPage;
